import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Action, Store} from "@ngrx/store";
import * as fromRoot from '../../../../state/app.state';
import {ClientDetailsService} from "./client-details.service";
import {Observable, of} from "rxjs";
import * as ClientDetailsActions from './client-details.actions';
import {catchError, map, mergeMap, tap} from "rxjs/operators";
import {FetchClientDetailsModel} from "../models/fetch-client-details.model";
import {AddClientDetailsModel} from "../models/add-client-details.model";
import {UpdateClientDetailsModel} from "../models/update-client-details.model";
import {
  FetchClientStatusFilterModel
} from "../models/fetch-client-status-filter.model";
import {
  FetchOrganizationTypeFilterModel
} from "../models/fetch-organization-type-filter.model";
import {CommonService} from "../../../shared/services/common.service";
import {Router} from "@angular/router";
import {DeleteClientModel} from "../models/delete-client.model";

@Injectable()
export class ClientDetailsEffects {

  fetchClientDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ClientDetailsActions.FetchClientDetailsRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap((payload: FetchClientDetailsModel['request']) =>
        this.clientDetailsService.fetchClientDetails(payload).pipe(
          map((response: FetchClientDetailsModel['response']) => {
            const {data, message} = response;
            const clientDetails = data;
            return ClientDetailsActions.FetchClientDetailsSuccess({
              clientDetails,
              message
            });
          }),
          catchError(() => {
            return of(ClientDetailsActions.FetchClientDetailsFailure());
          }),
          tap((action) => {
            if (action.type === ClientDetailsActions.FetchClientDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === ClientDetailsActions.FetchClientDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  addClientDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ClientDetailsActions.AddClientDetailsRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: AddClientDetailsModel['request']) =>
        this.clientDetailsService.addClientDetails(payload).pipe(
          map((response: AddClientDetailsModel['response']) => {
            const {data, message} = response;
            const clientDetails = data;
            return ClientDetailsActions.AddClientDetailsSuccess({
              clientDetails,
              allowInvite: ![1, 4].includes(payload.status),
              message
            });
          }),
          catchError(() => {
            return of(ClientDetailsActions.AddClientDetailsFailure());
          }),
          tap((action) => {
            if (action.type === ClientDetailsActions.AddClientDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.notification(
                action.message,
                'success'
              );
              if (action.allowInvite) {
                this.router.navigate([`/stanford-admin/invite`], {
                  queryParams: {
                    clientId: action?.clientDetails?.id
                  }
                });
              } else {
                this.router.navigate([`/stanford-admin/dashboard`], {
                  queryParams: {
                    dashboardId: 1
                  }
                });
              }
            } else if (action.type === ClientDetailsActions.AddClientDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
              this.clientDetailsService.enableAddClientForm.next(true);
            }
          })
        )
      )
    )
  );

  updateClientDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ClientDetailsActions.UpdateClientDetailsRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: UpdateClientDetailsModel['request']) =>
        this.clientDetailsService.updateClientDetails(payload).pipe(
          map((response: UpdateClientDetailsModel['response']) => {
            const {data, message} = response;
            const clientDetails = data;
            return ClientDetailsActions.UpdateClientDetailsSuccess({
              clientDetails,
              message
            });
          }),
          catchError(() => {
            return of(ClientDetailsActions.UpdateClientDetailsFailure());
          }),
          tap((action) => {
            if (action.type === ClientDetailsActions.UpdateClientDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.notification(
                action.message,
                'success'
              );

            } else if (action.type === ClientDetailsActions.UpdateClientDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchClientStatusFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ClientDetailsActions.FetchClientStatusFilterRequest),
      mergeMap(() =>
        this.clientDetailsService.fetchClientStatusFilter().pipe(
          map((response: FetchClientStatusFilterModel['response']) => {
            const {data, message} = response;
            const clientStatusFilter = data;
            return ClientDetailsActions.FetchClientStatusFilterSuccess({
              clientStatusFilter,
              message
            });
          }),
          catchError(() => {
            return of(ClientDetailsActions.FetchClientStatusFilterFailure());
          }),
          tap((action) => {
            if (action.type === ClientDetailsActions.FetchClientStatusFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === ClientDetailsActions.FetchClientStatusFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchOrgTypeFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ClientDetailsActions.FetchOrganizationTypeFilterRequest),
      mergeMap(() =>
        this.clientDetailsService.fetchOrgTypeListFilter().pipe(
          map((response: FetchOrganizationTypeFilterModel['response']) => {
            const {data, message} = response;
            const organizationTypeFilter = data;
            return ClientDetailsActions.FetchOrganizationTypeFilterSuccess({
              organizationTypeFilter,
              message
            });
          }),
          catchError(() => {
            return of(ClientDetailsActions.FetchOrganizationTypeFilterFailure());
          }),
          tap((action) => {
            if (action.type === ClientDetailsActions.FetchOrganizationTypeFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === ClientDetailsActions.FetchOrganizationTypeFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchClientName: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ClientDetailsActions.FetchClientNameRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: FetchClientDetailsModel['request']) =>
        this.clientDetailsService.fetchClientName(payload).pipe(
          map((response: FetchClientDetailsModel['response']) => {
            const {data, message} = response;
            return ClientDetailsActions.FetchClientNameSuccess({
              name: data.name,
              message
            });
          }),
          catchError(() => {
            return of(ClientDetailsActions.FetchClientNameFailure());
          }),
          tap((action) => {
            if (action.type === ClientDetailsActions.FetchClientNameSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === ClientDetailsActions.FetchClientNameFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  deleteClient$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(ClientDetailsActions.DeleteClientRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap((payload) =>
        this.clientDetailsService.deleteClient(payload).pipe(
          map((response: DeleteClientModel['response']) => {
            const {message} = response;
            return ClientDetailsActions.DeleteClientSuccess({
              message
            });
          }),
          catchError(() => {
            return of(ClientDetailsActions.DeleteClientFailure());
          }),
          tap((action) => {
            if (action.type === ClientDetailsActions.DeleteClientSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.closeDialog();
              const urlPrefix = this.router.url.split('/')[1];
              this.router.navigate([`/${urlPrefix}/dashboard`]);
              this.commonService.notification(
                action.message,
                'success'
              );

            } else if (action.type === ClientDetailsActions.DeleteClientFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  constructor(
    private commonService: CommonService,
    private store: Store<fromRoot.State>,
    private clientDetailsService: ClientDetailsService,
    private actions: Actions,
    private router: Router
  ) {
  }
}
